import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { sharesApi } from '../api/endpoints'
import { toast } from '../lib/toast'
import { ASKA_SERVICES_FEE_PERCENT } from '../lib/askaServices'
import { FLAT_PRICING } from '../lib/flatPricing'

/**
 * Share-with-markup dialog (#56/#57, Tej 2026-07-27). Opened from the results
 * table's selection bar — the broker sharing flow lives on the CATALOG now, not
 * the cart (the cart is Aska-purchases only). The broker picks a markup %, which
 * base it applies to (supplier price OR the Aska price after the service fee),
 * and whether to reveal stock ids; we create the share and hand back BOTH a
 * public link and an Excel (CSV) download of the marked-up list.
 */
export function ShareDialog({
  open, onClose, stockIds,
}: {
  open: boolean
  onClose: () => void
  stockIds: string[]
}) {
  const [markupPct, setMarkupPct] = useState('10')
  const [markupBase, setMarkupBase] = useState<'supplier' | 'aska'>('supplier')
  const [showStockId, setShowStockId] = useState(false)
  const [busy, setBusy] = useState(false)
  const [shareId, setShareId] = useState<string | null>(null)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)

  // Reset the form each time the dialog is (re)opened for a fresh selection.
  useEffect(() => {
    if (open) { setShareId(null); setShareLink(null); setCopied(false) }
  }, [open])

  // Esc closes.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  async function createShare() {
    if (busy || stockIds.length === 0) return
    setBusy(true)
    try {
      const pct = Math.max(0, Math.min(500, Number(markupPct) || 0))
      const res = await sharesApi.create({ stockIds, markupPercent: pct, markupBase, showStockId })
      const url = `${window.location.origin}/share/${res.shareId}`
      setShareId(res.shareId)
      setShareLink(url)
      try { await navigator.clipboard.writeText(url); setCopied(true) } catch { /* clipboard may be blocked */ }
      toast(`Share created (${res.count} stone${res.count === 1 ? '' : 's'})`, 'success')
    } catch {
      toast('Could not create the share — try again.', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function downloadExcel() {
    if (!shareId || downloading) return
    setDownloading(true)
    try {
      // A REAL .xlsx, rendered server-side by the SAME builder as the public
      // page (GET /api/shares/:id/xlsx) — prices can never drift from what
      // the link shows. Replaced the CSV-labeled-Excel (Tej 2026-09-20).
      const r = await fetch(`/api/shares/${encodeURIComponent(shareId)}/xlsx`)
      if (!r.ok) throw new Error(String(r.status))
      const blob = await r.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `diaex-share-${shareId}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
    } catch {
      toast('Could not build the Excel file — try again.', 'error')
    } finally {
      setDownloading(false)
    }
  }

  if (!open) return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm"
            onClick={onClose} aria-hidden
          />
          {/* Flex-centering WRAPPER — the panel is centred here, not via CSS
              translate. framer-motion writes its own inline `transform` for the
              y/scale animation, which would clobber a `-translate-x/y-1/2`
              centering offset and drop the dialog to the viewport's top-left
              corner (the "right + bottom" bug). pointer-events-none lets clicks
              on the empty area fall through to the overlay to close. */}
          <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            role="dialog"
            aria-label="Share with markup"
            data-testid="share-dialog"
            className="pointer-events-auto w-full max-w-[440px] rounded-2xl bg-paper p-6 shadow-elev-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-ink tracking-h2">Share with markup</h2>
                <p className="mt-0.5 text-[12px] text-soft">
                  {stockIds.length} stone{stockIds.length === 1 ? '' : 's'} · a link + Excel at your price
                </p>
              </div>
              <button
                type="button" onClick={onClose} aria-label="Close"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-soft hover:bg-page hover:text-ink transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </div>

            {!shareLink ? (
              <div className="mt-5 space-y-5">
                {/* Markup % */}
                <div className="flex items-center gap-3">
                  <label className="text-[13px] font-medium text-ink shrink-0 w-24">Your markup</label>
                  <div className="relative flex-1">
                    <input
                      type="number" min={0} max={500} step={1} value={markupPct}
                      onChange={(e) => setMarkupPct(e.target.value)}
                      className="h-10 w-full rounded-lg border border-border bg-paper pl-3 pr-8 text-sm text-ink tabular focus:border-gold focus:outline-none"
                      data-testid="share-markup"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-soft">%</span>
                  </div>
                </div>

                {/* Markup base (#57). Hidden under FLAT pricing — the "Aska
                    price" base equalled supplier × (1+fee%), and under the flat
                    model the displayed price IS the supplier price, so there is
                    only one base to mark up. */}
                {!FLAT_PRICING.live && (
                <div>
                  <p className="text-[13px] font-medium text-ink mb-2">Apply markup to</p>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Markup base">
                    <BaseOption
                      active={markupBase === 'supplier'} onClick={() => setMarkupBase('supplier')}
                      title="Supplier price" sub="Raw supplier cost"
                      testid="share-base-supplier"
                    />
                    <BaseOption
                      active={markupBase === 'aska'} onClick={() => setMarkupBase('aska')}
                      title="Aska price" sub={`After ${ASKA_SERVICES_FEE_PERCENT}% service fee`}
                      testid="share-base-aska"
                    />
                  </div>
                </div>
                )}

                {/* Show stock ids */}
                <label className="flex items-center gap-2 text-[13px] text-ink select-none cursor-pointer">
                  <input
                    type="checkbox" checked={showStockId} onChange={(e) => setShowStockId(e.target.checked)}
                    className="h-4 w-4 accent-navy" data-testid="share-showid"
                  />
                  Show stock IDs on the shared page
                </label>

                {/* Gold btn-primary — the dialog's single primary action,
                    matching the mobile share sheet's gold CTA vocabulary. */}
                <button
                  type="button" onClick={() => void createShare()} disabled={busy}
                  className="btn-primary w-full"
                  data-testid="share-create"
                >
                  {busy ? 'Creating…' : 'Create share'}
                </button>
              </div>
            ) : (
              <div className="mt-5 space-y-3" data-testid="share-result">
                <div className="rounded-lg border border-gold/40 bg-cream p-3">
                  <p className="text-[12px] font-semibold text-ink mb-1.5">
                    {copied ? 'Link copied to clipboard' : 'Your share link'}
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly value={shareLink}
                      className="h-9 flex-1 min-w-0 rounded border border-border bg-paper px-2 text-[12px] text-ink"
                      onFocus={(e) => e.target.select()} data-testid="share-link"
                    />
                    <a
                      href={shareLink} target="_blank" rel="noopener"
                      className="inline-flex h-9 items-center rounded-full bg-navy px-3.5 text-[12px] font-semibold text-white no-underline hover:bg-navy-hover"
                    >
                      Open
                    </a>
                  </div>
                </div>
                <button
                  type="button" onClick={() => void downloadExcel()} disabled={downloading}
                  className="inline-flex w-full items-center justify-center gap-2 h-11 rounded-full border border-line bg-paper text-sm font-semibold text-ink hover:border-gold/50 hover:bg-cream transition-colors disabled:opacity-50"
                  data-testid="share-download-excel"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {downloading ? 'Preparing…' : 'Download Excel'}
                </button>
                <button
                  type="button" onClick={onClose}
                  className="w-full text-center text-[12px] font-semibold text-soft hover:text-ink transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}

function BaseOption({
  active, onClick, title, sub, testid,
}: {
  active: boolean; onClick: () => void; title: string; sub: string; testid: string
}) {
  return (
    <button
      type="button" role="radio" aria-checked={active} onClick={onClick} data-testid={testid}
      className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
        active ? 'border-gold bg-gold/5' : 'border-border bg-paper hover:border-navy-300'
      }`}
    >
      <p className="text-[13px] font-semibold text-ink">{title}</p>
      <p className="text-[11px] text-soft mt-0.5">{sub}</p>
    </button>
  )
}
